import { observer } from 'mobx-react-lite';
import React from 'react';
import { useLanguage } from '../../../../hooks/shared';
import {
  useBillingStore,
  useEventInstanceStore,
  useNavigationService,
  useUserStore,
} from '../../../../stores';
import { billingApi } from '../../api/billingApi';
import { AccountType, Payment, TransactionEventType } from '../../api/types';

interface Props {
  payment: Payment;
  workspaceSlug: string;
  isAdmin: boolean;
}

const TransactionItem: React.FC<Props> = observer(
  ({ payment, workspaceSlug, isAdmin }) => {
    const { t } = useLanguage();
    const billingStore = useBillingStore();
    const eventInstanceStore = useEventInstanceStore();
    const navigationService = useNavigationService();
    const userStore = useUserStore();

    // Local loading state for this specific transaction
    const [isProcessing, setIsProcessing] = React.useState(false);

    const eventInfo =
      payment.typeId === TransactionEventType.EventCharge && payment.externalId
        ? billingApi.parseExternalId(payment.externalId)
        : null;

    const eventInstance = eventInfo
      ? eventInstanceStore.instances.find(
          (inst: any) => inst.id === eventInfo.instanceId
        )
      : null;

    let transactionUserId = eventInfo?.userId || billingStore.currentUserId;
    if (
      (payment.typeId === TransactionEventType.Deposit ||
        payment.typeId === TransactionEventType.SelfCharge) &&
      payment.transactions[0]
    ) {
      const transaction = payment.transactions[0];
      const accountData = Array.from(billingStore.userAccounts.values()).find(
        (data) => data.accounts.some((acc) => acc.id === transaction.accountId)
      );
      if (accountData) transactionUserId = accountData.userId;
    }

    const transactionUser = transactionUserId
      ? userStore.users.get(transactionUserId)
      : null;

    let depositStatus: 'pending' | 'approved' | 'cancelled' | null = null;
    if (
      payment.typeId === TransactionEventType.Deposit &&
      payment.transactions[0]
    ) {
      const transaction = payment.transactions[0];
      const accountData = billingStore.userAccounts.get(
        transactionUserId || ''
      );
      const account = accountData?.accounts.find(
        (acc) => acc.id === transaction.accountId
      );
      if (account) {
        if (transaction.isRolledBack) {
          depositStatus = 'cancelled';
        } else {
          depositStatus =
            account.typeId === AccountType.Pending ? 'pending' : 'approved';
        }
      }
    }

    const isClickable =
      payment.typeId === TransactionEventType.EventCharge && eventInstance;
    const isPendingDeposit = depositStatus === 'pending';
    const isCancelledDeposit = depositStatus === 'cancelled';

    return (
      <div
        className={`p-4 bg-gray-50 rounded-lg transition-colors ${
          isClickable ? 'hover:bg-gray-100 cursor-pointer' : 'hover:bg-gray-100'
        } ${isCancelledDeposit ? 'opacity-60 bg-gray-100' : ''}`}
        onClick={() => {
          if (isClickable && eventInstance && workspaceSlug) {
            navigationService.toTraining(workspaceSlug, eventInstance.id);
          }
        }}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4 flex-1">
            <div
              className={`w-10 h-10 rounded-full flex items-center justify-center ${
                isCancelledDeposit ? 'bg-gray-200' : 'bg-blue-100'
              }`}
            >
              <span
                className={`text-sm ${
                  isCancelledDeposit ? 'text-gray-500' : 'text-blue-600'
                }`}
              >
                {payment.typeId === TransactionEventType.Deposit
                  ? '⬆️'
                  : payment.typeId === TransactionEventType.EventCharge
                    ? '🏃'
                    : '⬇️'}
              </span>
            </div>
            <div className="flex-1">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-gray-900">
                    {billingStore.getTransactionTypeDisplayName(payment.typeId)}
                    {isClickable && (
                      <span className="ml-2 text-blue-600 text-sm">→</span>
                    )}
                    {payment.typeId === TransactionEventType.Deposit &&
                      depositStatus && (
                        <span
                          className={`ml-2 text-lg ${depositStatus === 'pending' ? 'text-orange-600' : depositStatus === 'cancelled' ? 'text-red-600' : 'text-green-600'}`}
                        >
                          {depositStatus === 'pending'
                            ? '⏳'
                            : depositStatus === 'cancelled'
                              ? '❌'
                              : '✅'}
                        </span>
                      )}
                  </p>
                </div>
              </div>
              <p className="text-sm text-gray-500">
                {new Date(payment.createdAt).toLocaleDateString()}
              </p>

              <div className="mt-1 space-y-1 max-w-full">
                {transactionUser &&
                  (isAdmin ||
                    payment.typeId === TransactionEventType.Deposit ||
                    payment.typeId === TransactionEventType.SelfCharge) && (
                    <p className="text-xs text-gray-600">
                      👤 {transactionUser.name}
                    </p>
                  )}

                {eventInstance && (
                  <p className="text-xs text-blue-600">
                    📅 {new Date(eventInstance.startTime).toLocaleDateString()}{' '}
                    ⏰
                    {new Date(eventInstance.startTime).toLocaleTimeString(
                      'ru-RU',
                      { hour: '2-digit', minute: '2-digit' }
                    )}
                  </p>
                )}

                {payment.transactions[0]?.description && (
                  <div
                    className="text-xs text-gray-600 cursor-help flex items-start"
                    style={{ maxWidth: '150px' }}
                    title={payment.transactions[0].description}
                  >
                    <span className="mr-1">💬</span>
                    <span
                      className="overflow-hidden line-clamp-2"
                      style={{
                        lineHeight: '1.2',
                        wordBreak: 'break-word',
                      }}
                    >
                      {payment.transactions[0].description}
                    </span>
                  </div>
                )}
              </div>
            </div>

            <div className="text-right">
              <div>
                {payment.transactions.map((transaction) => {
                  const isPositive =
                    payment.typeId === TransactionEventType.Deposit
                      ? true
                      : false;
                  const amount = Math.round(Number(transaction.amount));
                  return (
                    <div key={transaction.id}>
                      <p
                        className={`font-semibold ${
                          isCancelledDeposit
                            ? 'text-gray-400'
                            : isPositive
                              ? 'text-green-600'
                              : 'text-red-600'
                        }`}
                      >
                        {isPositive ? '+' : '−'}
                        {amount}&nbsp;₽
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        {isPendingDeposit && isAdmin && payment.transactions[0] && (
          <div className="mt-3 pt-3 border-t border-gray-200">
            <div className="flex justify-start space-x-3">
              <button
                onClick={async (e) => {
                  e.stopPropagation();
                  setIsProcessing(true);
                  try {
                    await billingStore.approveDeposit(
                      payment.transactions[0].eventId
                    );
                  } finally {
                    setIsProcessing(false);
                  }
                }}
                disabled={isProcessing}
                className="px-4 py-2 bg-green-600 text-white text-sm rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isProcessing ? t('billing.processing') : t('billing.confirm')}
              </button>
              <button
                onClick={async (e) => {
                  e.stopPropagation();
                  setIsProcessing(true);
                  try {
                    await billingStore.cancelDeposit(
                      payment.transactions[0].eventId
                    );
                  } finally {
                    setIsProcessing(false);
                  }
                }}
                disabled={isProcessing}
                className="px-4 py-2 bg-red-600 text-white text-sm rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isProcessing ? t('billing.processing') : t('billing.cancel')}
              </button>
            </div>
          </div>
        )}

        {isPendingDeposit && !isAdmin && payment.transactions[0] && (
          <div className="mt-3 pt-3 border-t border-gray-200">
            <div className="flex justify-start">
              <button
                onClick={async (e) => {
                  e.stopPropagation();
                  setIsProcessing(true);
                  try {
                    await billingStore.cancelDeposit(
                      payment.transactions[0].eventId
                    );
                  } finally {
                    setIsProcessing(false);
                  }
                }}
                disabled={isProcessing}
                className="px-4 py-2 bg-red-600 text-white text-sm rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isProcessing ? t('billing.processing') : t('billing.cancel')}
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }
);

export default TransactionItem;
